// Polyfills
import 'core-js/features/object/has-own'
import 'core-js/features/string/replace-all'

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import '@/assets/css/style.css'
import '@/assets/scss/bootstrap.scss'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createBootstrap } from 'bootstrap-vue-next'
import App from './App.vue'
import router from './router'
import store from './store'
import { guardRouteAccess } from './router/RouterAccessGuard'
import { ClickOutside } from './directives/ClickOutside'
import { Mask } from './directives/Mask'
import { library as faLibrary } from '@fortawesome/fontawesome-svg-core'
import { fab } from '@fortawesome/free-brands-svg-icons'
import { far } from '@fortawesome/pro-regular-svg-icons'
import { fas } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon, FontAwesomeLayers, FontAwesomeLayersText } from '@fortawesome/vue-fontawesome'
import 'moment'
import moment from 'moment-timezone'
import momentDurationFormatSetup from 'moment-duration-format'
import { extendMoment } from 'moment-range'
import constants from '@/constants'
import PrimeVue from 'primevue/config'
import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'
import Tooltip from 'primevue/tooltip'
import { initActivityMonitor } from './services/ActivityMonitorService'
import { initSizeMonitor } from './services/SizeMonitorService'
import { registerJwtServiceWithStore } from './services/JwtService'
import { registerUserProfileWithStore } from './services/UserProfileService'
import { registerOrgServiceWithStore } from './services/OrgService'
import { registerFormatPreferencesWithStore } from './services/FormatPreferencesService'
import { registerDashboardNotificationsWithStore } from './services/NotificationService'
import { logEvent } from '@/services/clients/firebase'
import { i18n } from './i18n-setup'
import FormGroup from './components/form/FormGroup.vue'
import ConfirmationService from 'primevue/confirmationservice';
import DialogService from 'primevue/dialogservice'
import ToastService from 'primevue/toastservice'
import { configureToast } from './utils/toast'
import _ from 'lodash'

// fonts
import "@fontsource/open-sans/latin-400.css"
import "@fontsource/open-sans/latin-700.css"

const oConstants = constants()

// Explictly import polyfills that vue cli 3 doesn't automatically polyfill,
// since it uses the older core-js 2. This should be fixed in vue cli 4:
// https://github.com/vuejs/vue-cli/issues/3834#issuecomment-484349452
// import 'core-js/stable/array/flat-map'

const bugsnagVue = window.Bugsnag?.getPlugin?.('vue')

const development = oConstants.development

if (/(?:\?|&)to-legacy(?:=|&|$)/.test(window.location.search)) {
  console.log('Detected to-legacy query parameter')
  logEvent('console_downgrade_to_legacy')
}

// moment-duration-format automatically installs on default moment instance.
// we need to manually install it on moment-timezone.
momentDurationFormatSetup(moment)
// Set defaults.
Object.assign(moment.duration.fn.format.defaults, {
  trim: false,
  round: true
})
extendMoment(moment)

faLibrary.add(fab, far, fas)

const pinia = createPinia()

const PrimeVuePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    }
  }
})
const app = createApp(App)
  .component('font-awesome-icon', FontAwesomeIcon)
  .component('font-awesome-layers', FontAwesomeLayers)
  .component('font-awesome-layers-text', FontAwesomeLayersText)
  .component('FormGroup', FormGroup)
  .directive('click-outside', ClickOutside)
  .directive('tooltip', Tooltip)
  .directive('mask', Mask)

if (bugsnagVue) {
  app.use(bugsnagVue)
}

app
  .use(createBootstrap())
  .use(PrimeVue, {
    theme: {
      preset: PrimeVuePreset,
      options: {
        prefix: 'p',
        darkModeSelector: 'app-dark',
        cssLayer: {
          name: 'primevue',
          // Keep bootstrap ahead of primevue utilities and place tailwind utilities last.
          order: 'theme, bootstrap, bootstrap-vue, bootstrap-overrides, legacy, primevue, components, utilities'
        }
      }
    }
  })
  .use(ConfirmationService)
  .use(DialogService)
  .use(ToastService)
  .use(pinia)
  .use(router)
  .use(store)
  .use(i18n)

initActivityMonitor(store)
initSizeMonitor(store)
registerJwtServiceWithStore(store)
registerUserProfileWithStore(store)
registerOrgServiceWithStore(store)
registerFormatPreferencesWithStore(store)
registerDashboardNotificationsWithStore(store)
guardRouteAccess(router, store)

app.mount('#app')

Object.assign(app.config.globalProperties, {
  // Provide easy way to append a path in a route-link:
  // https://router.vuejs.org/guide/migration/#Removal-of-append-prop-in-router-link-
  append: (path, pathToAppend) =>
    path + (path.endsWith('/') ? '' : '/') + pathToAppend,
  _$: _
})

configureToast(app)

// TODO: Inject the following development code from a separate module using build-time script.
if (development) {
  window.fc$ = { moment, _ }

  // Add development tooling to window object.
  import('deep-object-diff').then(module => {
    window.fc$.diff = module
  })
  import('inflection').then(module => {
    window.fc$.inflection = module
  })
  import('luxon').then(module => {
    window.fc$.luxon = module
  })
  // Add ability to break in debugger if a particular property changes.
  // It became useful to debug SyncFusion.
  // https://stackoverflow.com/a/11658693/1237919
  console.watch = function(oObj, sProp) {
    var sPrivateProp = "$_"+sProp+"_$" // to minimize the name clash risk
    oObj[sPrivateProp] = oObj[sProp]

    // overwrite with accessor
    Object.defineProperty(oObj, sProp, {
        get: function () {
            return oObj[sPrivateProp]
        },

        set: function (value) {
            //console.log("setting " + sProp + " to " + value);
            debugger // sets breakpoint
            oObj[sPrivateProp] = value
        }
    })
  }
}
